import type {
    BuildSummary,
    Configuration,
    ForkliftModel,
    OptionKey,
    PaintChoice
} from '@/types'

import { createStore } from '@/lib/store'

import {
    EXTENDED_RUNTIME_FACTOR,
    LEASE_FACTOR,
    batteries,
    options,
    paints,
    tires
} from '@/constants/lineup'

export type TruckLook = {
    /** Overall size of the 3D truck, derived from the rated capacity. */
    scale: number
    /** Three-stage mast for the taller lift heights. */
    triplex: boolean
    /** Model name painted on the counterweight. */
    label: string
    /** Paint color of the body panels. */
    paint: string
}

export type ConfigState = Configuration & {
    truck: TruckLook
    /** Last option the visitor switched on, so the 3D truck can play a short demo of it. */
    pulse: { key: OptionKey | 'paint' | 'model' | 'tires', at: number } | null
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export const paintFor = (key: string): PaintChoice => paints.find(paint => paint.key === key) ?? paints[0]

export const truckFor = (model: ForkliftModel | undefined, paint: string): TruckLook => ({
    scale: model ? clamp(1 + (model.capacityKg - 3000) * 0.00007, 0.88, 1.18) : 1,
    triplex: model ? model.liftHeightM > 5 : true,
    label: model ? model.name : "FQ-30",
    paint: paintFor(paint).hex
})

export const configStore = createStore<ConfigState>({
    modelSlug: null,
    paint: 'yellow',
    battery: 'standard',
    tires: 'cushion',
    options: [],
    truck: truckFor(undefined, 'yellow'),
    pulse: null
})

/** The build the visitor asked a quote for, picked up by the quote form. */
export const quoteStore = createStore<{ build: BuildSummary | null, version: number }>({
    build: null,
    version: 0
})

export const defaultModel = (models: ForkliftModel[]): ForkliftModel | undefined => {
    return models.find(model => model.badge) ?? models[1] ?? models[0]
}

export const selectedModel = (models: ForkliftModel[], slug: string | null): ForkliftModel | undefined => {
    return models.find(model => model.slug === slug) ?? defaultModel(models)
}

export const runtimeFor = (model: ForkliftModel, battery: Configuration['battery']): number => {
    return Math.round(model.runtimeHours * (battery === 'extended' ? EXTENDED_RUNTIME_FACTOR : 1))
}

export type PriceLine = { label: string, priceUsd: number }

export const priceLines = (model: ForkliftModel, config: Configuration): PriceLine[] => {
    const paint = paintFor(config.paint)
    const battery = batteries.find(item => item.key === config.battery) ?? batteries[0]
    const tire = tires.find(item => item.key === config.tires) ?? tires[0]
    const extras = options.filter(option => config.options.includes(option.key))

    return [
        { label: `${model.name} ${model.edition}`, priceUsd: model.priceUsd },
        { label: paint.name, priceUsd: paint.priceUsd },
        { label: battery.name, priceUsd: battery.priceUsd },
        { label: tire.name, priceUsd: tire.priceUsd },
        ...extras.map(option => ({ label: option.name, priceUsd: option.priceUsd }))
    ]
}

export const totalFor = (model: ForkliftModel, config: Configuration): number => {
    return priceLines(model, config).reduce((sum, line) => sum + line.priceUsd, 0)
}

export const leaseFor = (total: number): number => Math.round((total * LEASE_FACTOR) / 5) * 5

export const buildSummary = (model: ForkliftModel, config: Configuration): BuildSummary => ({
    modelSlug: model.slug,
    modelName: `${model.name} ${model.edition}`,
    lines: priceLines(model, config).slice(1).map(line => line.label),
    totalUsd: totalFor(model, config),
    configuration: {
        modelSlug: model.slug,
        paint: config.paint,
        battery: config.battery,
        tires: config.tires,
        options: [...config.options]
    }
})
